import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import MobileHeader from '@/components/mobile/MobileHeader';
import Footer from '@/components/Footer';
import { useIsMobile } from '@/hooks/useIsMobile';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { FileText, Clock, CheckCircle, AlertCircle, Search } from 'lucide-react';
import { useUserRole } from '@/hooks/useUserRole';
import { toast } from 'sonner';
import { formatGameDateTime, getUserTimezone } from '@/utils/timezone';
import { calculateGameStatus, getGameStatusInfo, getGameDateTime } from '@/utils/gameStatusUtils';

import { supabase } from '@/integrations/supabase/client';

interface GameForm {
  id: string;
  game_title: string;
  game_date: string;
  game_time: string;
  game_timezone?: string;
  game_status?: string;
  game_code?: string;
  venue_name?: string;
  home_team_name?: string;
  away_team_name?: string;
  form_status: 'pending' | 'in_progress' | 'completed' | 'needs_approval';
  user_role_in_game: string;
  deadline?: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  game_type: 'regular' | 'venue';
}

export const MyGameFormsPage: React.FC = () => {
  const [games, setGames] = useState<GameForm[]>([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState('active');
  
  const { permissions, userId, userType } = useUserRole();
  const navigate = useNavigate();

  useEffect(() => {
    loadUserGameForms();
  }, [userId, permissions]);

  const loadUserGameForms = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      const userGameForms: GameForm[] = [];

      console.log('🔍 Loading game forms for user:', userId, 'type:', userType);
      console.log('📝 User permissions:', permissions);

      // Load ALL games created by the user first (most important)
      try {
        const regularGamesResponse: any = await supabase
          .from('games')
          .select(`
            id, game_name, game_date, game_time, game_timezone, game_code, form_sections_completed, 
            home_team_id, away_team_id, created_by, game_status, venue_id,
            home_team:teams!games_home_team_id_fkey(team_name),
            away_team:teams!games_away_team_id_fkey(team_name)
          `)
          .or(`created_by.eq.${userId},venue_id.eq.${userId}`);

        console.log('🎮 Regular games response:', regularGamesResponse);

        if (regularGamesResponse.data) {
          regularGamesResponse.data.forEach((game: any) => {
            const homeTeamName = game.home_team?.team_name || 'Home Team';
            const awayTeamName = game.away_team?.team_name || 'Away Team';
            const gameTitle = game.game_name || `${homeTeamName} vs ${awayTeamName}`;
            
            userGameForms.push({
              id: game.id,
              game_title: gameTitle,
              game_date: game.game_date,
              game_time: game.game_time,
              game_timezone: game.game_timezone,
              game_status: game.game_status,
              game_code: game.game_code,
              form_status: determineFormStatus(game.form_sections_completed, game.created_by === userId ? 'coach' : 'venue'),
              user_role_in_game: game.created_by === userId ? 'coach' : 'venue',
              priority: 'normal',
              game_type: 'regular'
            });
          });
        }
      } catch (error) {
        console.error('❌ Error loading regular games:', error);
      }

      // Load venue games for coaches/venues
      if (permissions.isCoach || permissions.isVenue) {
        try {
          const venueGamesResponse: any = await supabase
            .from('venue_games')
            .select(`
              id, game_title, game_date, game_time, game_timezone, game_code, form_sections_completed, 
              home_team_id, away_team_id, venue_id, posted_by, game_status,
              home_team:teams!venue_games_home_team_id_fkey(team_name),
              away_team:teams!venue_games_away_team_id_fkey(team_name)
            `)
            .or(`posted_by.eq.${userId},venue_id.eq.${userId}`);

          console.log('📍 Venue games response:', venueGamesResponse);

          if (venueGamesResponse.data) {
            venueGamesResponse.data.forEach((game: any) => {
              // Avoid duplicates
              if (!userGameForms.some(existing => existing.id === game.id)) {
                const homeTeamName = game.home_team?.team_name || 'Home Team';
                const awayTeamName = game.away_team?.team_name || 'Away Team';
                const gameTitle = game.game_title || `${homeTeamName} vs ${awayTeamName}`;
                
                userGameForms.push({
                  id: game.id,
                  game_title: gameTitle,
                  game_date: game.game_date,
                  game_time: game.game_time,
                  game_timezone: game.game_timezone,
                  game_status: game.game_status,
                  game_code: game.game_code,
                  form_status: determineFormStatus(game.form_sections_completed, 'coach'),
                  user_role_in_game: game.posted_by === userId ? 'coach' : 'venue',
                  priority: 'normal',
                  game_type: 'venue'
                });
              }
            });
          }
        } catch (error) {
          console.error('❌ Error loading venue games:', error);
        }
      }

      // Load games for teams the user coaches or owns (for coaches)
      if (permissions.isCoach) {
        try {
          // Get teams where user is a coach or owner
          const [coachTeamsResponse, ownedTeamsResponse]: any = await Promise.all([
            supabase.from('team_coaches').select('team_id').eq('coach_id', userId),
            supabase.from('teams').select('team_id').eq('created_by', userId)
          ]);

          const coachTeamIds = coachTeamsResponse.data?.map((t: any) => t.team_id) || [];
          const ownedTeamIds = ownedTeamsResponse.data?.map((t: any) => t.team_id) || [];
          const allTeamIds = [...new Set([...coachTeamIds, ...ownedTeamIds])];

          if (allTeamIds.length > 0) {
            // Get games for these teams
            const [teamVenueGamesResponse, teamRegularGamesResponse]: any = await Promise.all([
              supabase
                .from('venue_games')
                .select(`
                  id, game_title, game_date, game_time, game_timezone, game_code, form_sections_completed, home_team_id, away_team_id, game_status,
                  home_team:teams!venue_games_home_team_id_fkey(team_name),
                  away_team:teams!venue_games_away_team_id_fkey(team_name)
                `)
                .or(`home_team_id.in.(${allTeamIds.join(',')}),away_team_id.in.(${allTeamIds.join(',')})`),
              supabase
                .from('games')
                .select(`
                  id, game_name, game_date, game_time, game_timezone, game_code, form_sections_completed, home_team_id, away_team_id, game_status,
                  home_team:teams!games_home_team_id_fkey(team_name),
                  away_team:teams!games_away_team_id_fkey(team_name)
                `)
                .or(`home_team_id.in.(${allTeamIds.join(',')}),away_team_id.in.(${allTeamIds.join(',')})`),
            ]);

            // Process team venue games
            teamVenueGamesResponse.data?.forEach((game: any) => {
              if (!userGameForms.some(existing => existing.id === game.id)) {
                const homeTeamName = game.home_team?.team_name || 'Home Team';
                const awayTeamName = game.away_team?.team_name || 'Away Team';
                const gameTitle = game.game_title || `${homeTeamName} vs ${awayTeamName}`;
                
                userGameForms.push({
                  id: game.id,
                  game_title: gameTitle,
                  game_date: game.game_date,
                  game_time: game.game_time,
                  game_timezone: game.game_timezone,
                  game_status: game.game_status,
                  game_code: game.game_code,
                  form_status: determineFormStatus(game.form_sections_completed, 'coach'),
                  user_role_in_game: 'coach',
                  priority: 'normal',
                  game_type: 'venue'
                });
              }
            });

            // Process team regular games
            teamRegularGamesResponse.data?.forEach((game: any) => {
              if (!userGameForms.some(existing => existing.id === game.id)) {
                const homeTeamName = game.home_team?.team_name || 'Home Team';
                const awayTeamName = game.away_team?.team_name || 'Away Team';
                const gameTitle = game.game_name || `${homeTeamName} vs ${awayTeamName}`;
                
                userGameForms.push({
                  id: game.id,
                  game_title: gameTitle,
                  game_date: game.game_date,
                  game_time: game.game_time,
                  game_timezone: game.game_timezone,
                  game_status: game.game_status,
                  game_code: game.game_code,
                  form_status: determineFormStatus(game.form_sections_completed, 'coach'),
                  user_role_in_game: 'coach',
                  priority: 'normal',
                  game_type: 'regular'
                });
              }
            });
          }
        } catch (error) {
          console.error('❌ Error loading team games:', error);
        }
      }

      // Load games for players (both Supabase and Internal users)
      if (permissions.isPlayer) {
        try {
          console.log('🎮 Loading player games for userId:', userId, 'userType:', userType);
          
          // Get ALL participant games - confirmed AND requested
          const participantsResponse: any = await supabase
            .from('game_participants')
            .select('game_id, player_type, participation_status')
            .eq('player_id', userId)
            .eq('player_type', userType);

          // Also check game_rosters_live for players who might be in roster but not participants
          const rosterResponse: any = await supabase
            .from('game_rosters_live')
            .select('game_id, player_type')
            .eq('player_id', userId)
            .eq('player_type', userType);

          console.log('👥 Player games found:', participantsResponse.data?.length || 0);
          console.log('🏀 Roster games found:', rosterResponse.data?.length || 0);

          const confirmedGameIds = participantsResponse.data?.filter((p: any) => p.participation_status === 'confirmed').map((p: any) => p.game_id) || [];
          const requestedGameIds = participantsResponse.data?.filter((p: any) => p.participation_status === 'requested').map((p: any) => p.game_id) || [];
          
          // Add roster games that aren't already in participants (for legacy data or edge cases)
          const rosterGameIds = rosterResponse.data?.map((r: any) => r.game_id) || [];
          const additionalRosterGames = rosterGameIds.filter((gameId: string) => 
            !confirmedGameIds.includes(gameId) && !requestedGameIds.includes(gameId)
          );
          
          // Treat roster-only games as confirmed for form purposes
          confirmedGameIds.push(...additionalRosterGames);
          
          console.log('✅ Confirmed games:', confirmedGameIds);
          console.log('⏳ Requested games:', requestedGameIds);
          console.log('🏀 Additional roster games:', additionalRosterGames);

          // Legacy: Also check join_requests table for pending requests  
          const joinRequestsResponse: any = await supabase
            .from('join_requests')
            .select('game_id, player_type')
            .eq('player_id', userId)
            .eq('player_type', userType)
            .eq('request_status', 'pending');

          const legacyPendingGameIds = joinRequestsResponse.data?.map((r: any) => r.game_id) || [];
          console.log('📋 Legacy pending request games:', legacyPendingGameIds);
          
          // Combine all game IDs (remove duplicates)  
          const allGameIds = [...new Set([...confirmedGameIds, ...requestedGameIds, ...legacyPendingGameIds])];

          console.log('🎮 Player games - Confirmed:', confirmedGameIds.length, 'Requested:', requestedGameIds.length, 'Legacy pending:', legacyPendingGameIds.length, 'Total:', allGameIds.length);

          if (allGameIds.length > 0) {
            const [playerVenueGamesResponse, playerRegularGamesResponse]: any = await Promise.all([
              supabase
                .from('venue_games')
                .select(`
                  id, game_title, game_date, game_time, game_timezone, game_code, form_sections_completed, home_team_id, away_team_id, game_status,
                  home_team:teams!venue_games_home_team_id_fkey(team_name),
                  away_team:teams!venue_games_away_team_id_fkey(team_name)
                `)
                .in('id', allGameIds),
              supabase
                .from('games')
                .select(`
                  id, game_name, game_date, game_time, game_timezone, game_code, form_sections_completed, home_team_id, away_team_id, game_status,
                  home_team:teams!games_home_team_id_fkey(team_name),
                  away_team:teams!games_away_team_id_fkey(team_name)
                `)
                .in('id', allGameIds)
            ]);

            // Process player venue games
            playerVenueGamesResponse.data?.forEach((game: any) => {
              if (!userGameForms.some(existing => existing.id === game.id)) {
                const homeTeamName = game.home_team?.team_name || 'Home Team';
                const awayTeamName = game.away_team?.team_name || 'Away Team';
                const gameTitle = game.game_title || `${homeTeamName} vs ${awayTeamName}`;
                
                // Determine form status based on participation
                let formStatus: GameForm['form_status'] = 'pending';
                let priority: GameForm['priority'] = 'normal';
                
                if (confirmedGameIds.includes(game.id)) {
                  formStatus = determineFormStatus(game.form_sections_completed, 'player');
                } else if (requestedGameIds.includes(game.id) || legacyPendingGameIds.includes(game.id)) {
                  formStatus = 'needs_approval'; // Pending join request
                  priority = 'high';
                }
                
                console.log(`🎮 Adding venue game: ${game.game_code} - ${gameTitle} (Status: ${formStatus})`);
                
                userGameForms.push({
                  id: game.id,
                  game_title: gameTitle,
                  game_date: game.game_date,
                  game_time: game.game_time,
                  game_timezone: game.game_timezone,
                  game_status: game.game_status,
                  game_code: game.game_code,
                  form_status: formStatus,
                  user_role_in_game: 'player',
                  priority: priority,
                  game_type: 'venue'
                });
              }
            });

            // Process player regular games
            playerRegularGamesResponse.data?.forEach((game: any) => {
              if (!userGameForms.some(existing => existing.id === game.id)) {
                const homeTeamName = game.home_team?.team_name || 'Home Team';
                const awayTeamName = game.away_team?.team_name || 'Away Team';
                const gameTitle = game.game_name || `${homeTeamName} vs ${awayTeamName}`;
                
                // Determine form status based on participation
                let formStatus: GameForm['form_status'] = 'pending';
                let priority: GameForm['priority'] = 'normal';
                
                if (confirmedGameIds.includes(game.id)) {
                  formStatus = determineFormStatus(game.form_sections_completed, 'player');
                } else if (requestedGameIds.includes(game.id) || legacyPendingGameIds.includes(game.id)) {
                  formStatus = 'needs_approval'; // Pending join request
                  priority = 'high';
                }
                
                console.log(`🎮 Adding regular game: ${game.game_code} - ${gameTitle} (Status: ${formStatus})`);
                
                userGameForms.push({
                  id: game.id,
                  game_title: gameTitle,
                  game_date: game.game_date,
                  game_time: game.game_time,
                  game_timezone: game.game_timezone,
                  game_status: game.game_status,
                  game_code: game.game_code,
                  form_status: formStatus,
                  user_role_in_game: 'player',
                  priority: priority,
                  game_type: 'regular'
                });
              }
            });
          }
        } catch (error) {
          console.error('❌ Error loading player games:', error);
        }
      }

      console.log('📋 Total games loaded:', userGameForms.length);
      console.log('📋 Games with codes:', userGameForms.filter(g => g.game_code).map(g => ({id: g.id.slice(-6), code: g.game_code, title: g.game_title})));
      
      setGames(userGameForms);
    } catch (error) {
      console.error('❌ Error loading game forms:', error);
      toast.error('Failed to load game forms');
    } finally {
      setLoading(false);
    }
  };

  const determineFormStatus = (sectionsCompleted: any, userRole: string): GameForm['form_status'] => {
    if (!sectionsCompleted) return 'pending';
    
    const sections = typeof sectionsCompleted === 'string' 
      ? JSON.parse(sectionsCompleted) 
      : sectionsCompleted;

    switch (userRole) {
      case 'player':
        return sections?.players ? 'completed' : 'pending';
      case 'coach':
        return sections?.teams ? 'completed' : 'pending';
      case 'venue':
        return sections?.venue ? 'completed' : 'pending';
      default:
        return 'pending';
    }
  };

  const getStatusIcon = (status: GameForm['form_status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'in_progress':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'needs_approval':
        return <AlertCircle className="h-4 w-4 text-blue-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: GameForm['form_status']) => {
    const variants = {
      completed: 'default',
      in_progress: 'secondary',
      needs_approval: 'outline',
      pending: 'destructive'
    } as const;

    const labels = {
      completed: 'COMPLETED',
      in_progress: 'IN PROGRESS',
      needs_approval: 'PENDING APPROVAL',
      pending: 'PENDING'
    } as const;

    return (
      <Badge variant={variants[status]}>
        {labels[status]}
      </Badge>
    );
  };

  // Split games into active and expired based on game status
  const activeGames = games.filter(game => {
    const gameStatus = calculateGameStatus(game.game_date, game.game_time, game.game_status, 2, game.game_timezone || 'UTC');
    
    // Use timezone-aware comparison instead of UTC string comparison
    const gameDateTime = getGameDateTime(game.game_date, game.game_time, game.game_timezone || 'UTC');
    const now = new Date();
    
    // If game date/time has passed in its timezone, it's expired
    if (gameDateTime < now) {
      return false;
    }
    
    // Exclude all expired statuses including cancelled and scheduled old games
    return gameStatus !== 'expired' && 
           gameStatus !== 'missed' && 
           gameStatus !== 'completed' && 
           gameStatus !== 'needs_scoring' &&
           gameStatus !== 'cancelled' &&
           gameStatus !== 'scheduled';
  });
  
  const expiredGames = games.filter(game => {
    const gameStatus = calculateGameStatus(game.game_date, game.game_time, game.game_status, 2, game.game_timezone || 'UTC');
    
    // Add absolute date cutoff - any game before today should be expired for form purposes
    // Use string comparison to avoid timezone conversion issues
    const todayStr = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
    
    // If game is before today, it's expired regardless of status
    if (game.game_date < todayStr) {
      return true;
    }
    
    // Include all expired statuses including cancelled and scheduled
    return gameStatus === 'expired' || 
           gameStatus === 'missed' || 
           gameStatus === 'completed' || 
           gameStatus === 'needs_scoring' ||
           gameStatus === 'cancelled' ||
           gameStatus === 'scheduled';
  });

  // Filter function for games
  const filterGames = (gamesList: GameForm[]) => {
    return gamesList.filter(game => {
      const searchLower = searchTerm.toLowerCase().trim();
      
      // Enhanced search - check multiple fields
      const matchesSearch = searchTerm === '' || 
        game.game_title.toLowerCase().includes(searchLower) ||
        (game.game_code && game.game_code.toLowerCase().includes(searchLower)) ||
        game.id.toLowerCase().includes(searchLower) ||
        game.id.slice(-6).toLowerCase().includes(searchLower) ||
        (game.home_team_name && game.home_team_name.toLowerCase().includes(searchLower)) ||
        (game.away_team_name && game.away_team_name.toLowerCase().includes(searchLower)) ||
        (game.venue_name && game.venue_name.toLowerCase().includes(searchLower));
      
      const matchesStatus = statusFilter === 'all' || game.form_status === statusFilter;
      const matchesPriority = priorityFilter === 'all' || game.priority === priorityFilter;
      
      // Debug logging for search issues
      if (searchTerm && searchLower === '5303ec') {
        console.log('🔍 Debug search for 5303EC:', {
          gameId: game.id.slice(-6),
          gameCode: game.game_code,
          gameTitle: game.game_title,
          searchTerm: searchLower,
          matchesSearch,
          matchesStatus,
          matchesPriority
        });
      }
      
      return matchesSearch && matchesStatus && matchesPriority;
    });
  };

  const filteredActiveGames = filterGames(activeGames);
  const filteredExpiredGames = filterGames(expiredGames);

  const handleGameFormClick = (game: GameForm) => {
    // Check if game is expired before allowing navigation
    const gameStatus = calculateGameStatus(
      game.game_date, 
      game.game_time, 
      game.game_status, 
      2, 
      game.game_timezone || getUserTimezone()
    );
    const isExpired = gameStatus === 'expired' || gameStatus === 'missed' || gameStatus === 'completed' || gameStatus === 'needs_scoring';
    
    if (isExpired) {
      toast.error('This game form is no longer available - the game time has passed.');
      return;
    }
    
    navigate(`/games/${game.id}/unified-form?type=${game.game_type}`);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {isMobile ? <MobileHeader /> : <Header />}
      
      <main className="flex-1 pt-20">
        <div className="container mx-auto py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-4">My Game Forms</h1>
            <p className="text-muted-foreground">
              Manage and complete your assigned game forms based on your role in each game.
            </p>
          </div>

          {/* Filters */}
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by title, ID, code, teams, or venue..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="needs_approval">Needs Approval</SelectItem>
              </SelectContent>
            </Select>

            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Game Forms Tabs */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-muted-foreground">Loading your game forms...</p>
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="active">
                  Active Forms ({filteredActiveGames.length})
                </TabsTrigger>
                <TabsTrigger value="expired">
                  Expired Forms ({filteredExpiredGames.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="active" className="mt-6">
                {filteredActiveGames.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center">
                      <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No Active Game Forms</h3>
                      <p className="text-muted-foreground">
                        {activeGames.length === 0 
                          ? "You don't have any active game forms at the moment."
                          : "No active game forms match your current filters."
                        }
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {filteredActiveGames.map((game) => (
                      <Card key={game.id} className="cursor-pointer hover:shadow-md transition-shadow">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="flex items-center gap-2">
                                {getStatusIcon(game.form_status)}
                                {game.game_title}
                              </CardTitle>
                              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                                <span>{formatGameDateTime(game.game_date, game.game_time, game.game_timezone || getUserTimezone(), getUserTimezone()).fullDateTime}</span>
                                {game.venue_name && <span>• {game.venue_name}</span>}
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <div className="flex items-center gap-2">
                                {game.game_code && (
                                  <Badge variant="secondary" className="font-mono text-xs">
                                    {game.game_code}
                                  </Badge>
                                )}
                                {getStatusBadge(game.form_status)}
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">{game.user_role_in_game}</Badge>
                                <span className="text-xs text-muted-foreground font-mono" title={`Game ID: ${game.id}`}>
                                  ID: {game.id.slice(-6)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        
                        <CardContent>
                          <div className="flex justify-between items-center">
                            <div className="text-sm text-muted-foreground">
                              Form type: {game.game_type} game
                            </div>
                            <Button 
                              onClick={() => handleGameFormClick(game)}
                              size="sm"
                            >
                              {game.form_status === 'completed' ? 'View Form' : 'Complete Form'}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="expired" className="mt-6">
                {filteredExpiredGames.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center">
                      <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No Expired Game Forms</h3>
                      <p className="text-muted-foreground">
                        {expiredGames.length === 0 
                          ? "You don't have any expired game forms."
                          : "No expired game forms match your current filters."
                        }
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {filteredExpiredGames.map((game) => (
                      <Card key={game.id} className="cursor-pointer hover:shadow-md transition-shadow opacity-75">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="flex items-center gap-2">
                                {getStatusIcon(game.form_status)}
                                {game.game_title}
                                <Badge variant="destructive" className="ml-2 text-xs">
                                  No Longer Available
                                </Badge>
                              </CardTitle>
                              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                                <span>{formatGameDateTime(game.game_date, game.game_time, game.game_timezone || getUserTimezone(), getUserTimezone()).fullDateTime}</span>
                                {game.venue_name && <span>• {game.venue_name}</span>}
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <div className="flex items-center gap-2">
                                {game.game_code && (
                                  <Badge variant="secondary" className="font-mono text-xs">
                                    {game.game_code}
                                  </Badge>
                                )}
                                {getStatusBadge(game.form_status)}
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">{game.user_role_in_game}</Badge>
                                <span className="text-xs text-muted-foreground font-mono" title={`Game ID: ${game.id}`}>
                                  ID: {game.id.slice(-6)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        
                        <CardContent>
                          <div className="flex justify-between items-center">
                            <div className="text-sm text-muted-foreground">
                              Form type: {game.game_type} game
                            </div>
                            <Button 
                              onClick={() => handleGameFormClick(game)}
                              size="sm"
                              variant="secondary"
                              disabled
                            >
                              No Longer Available
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};